import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Palette } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADER_FIELDS = [
  { key: 'header_title', label: 'Başlık', placeholder: 'AVM Belge Takip' },
  { key: 'header_subtitle', label: 'Alt başlık', placeholder: 'İSG Yönetim Sistemi' },
];
const FIELDS = [
  { key: 'unit_name', label: 'Birim adı', placeholder: 'İSG Birimi' },
  { key: 'phone', label: 'Telefon', placeholder: '0212 000 00 00' },
  { key: 'email', label: 'E-posta', placeholder: 'isg@avm.com' },
  { key: 'address', label: 'Adres', placeholder: 'Merkez AVM, Yönetim Katı' },
  { key: 'footer_note', label: 'Alt bilgi notu', placeholder: 'Bu e-posta ... otomatik gönderilmiştir.', wide: true },
];

const FieldGrid = ({ fields, form, set }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {fields.map(f => (
      <div key={f.key} className={`space-y-1 ${f.wide ? 'sm:col-span-2' : ''}`}>
        <Label htmlFor={`branding-${f.key}`}>{f.label}</Label>
        <Input id={`branding-${f.key}`} value={form[f.key] || ''} placeholder={f.placeholder} onChange={(e) => set({ [f.key]: e.target.value })} data-testid={`email-branding-${f.key}`} />
      </div>
    ))}
  </div>
);
const PRESETS = ['#0f172a', '#1d4ed8', '#047857', '#b45309', '#be123c', '#6d28d9'];

export const EmailBrandingCard = () => {
  const [form, setForm] = useState(null);
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/email-branding`).then(res => setForm(res.data)).catch(() => setForm({ header_color: '#0f172a' }));
  }, []);

  useEffect(() => {
    if (!form || !/^#[0-9a-fA-F]{6}$/.test(form.header_color || '')) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/settings/email-branding/preview`, form).then(res => setHtml(res.data.html)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [form]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/email-branding`, form);
      setForm(res.data);
      toast.success('E-posta görünümü kaydedildi; tüm e-postalar bu tasarımla gönderilecek');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <Card className="border-slate-200" data-testid="email-branding-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5" />E-posta Görünümü</CardTitle>
        <CardDescription>Sistemden giden tüm e-postalar bu ortak tasarımla gönderilir: renkli başlık, içerik ve altta İSG birimi iletişim bilgileri.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Üst başlık</p>
        <FieldGrid fields={HEADER_FIELDS} form={form} set={set} />
        <div className="space-y-2">
          <Label>Başlık rengi</Label>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="color" value={form.header_color} onChange={(e) => set({ header_color: e.target.value })} className="h-9 w-12 rounded border cursor-pointer" data-testid="email-branding-color" />
            <Input value={form.header_color} onChange={(e) => set({ header_color: e.target.value })} className="w-28 font-mono" data-testid="email-branding-color-hex" />
            {PRESETS.map(c => (
              <button key={c} type="button" onClick={() => set({ header_color: c })} className={`h-7 w-7 rounded-full border-2 ${form.header_color === c ? 'border-slate-900' : 'border-white shadow'}`} style={{ background: c }} title={c} data-testid={`email-branding-preset-${c.slice(1)}`} />
            ))}
          </div>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-2">Alt bilgi (iletişim)</p>
        <FieldGrid fields={FIELDS} form={form} set={set} />
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-500">Canlı önizleme</p>
          <iframe title="E-posta görünümü önizleme" srcDoc={html} sandbox="" className="w-full h-80 rounded border bg-white" data-testid="email-branding-preview" />
        </div>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="email-branding-save">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Kaydet
        </Button>
      </CardContent>
    </Card>
  );
};
